import { useState } from 'react';
import { isClosed, STAGES, stageLabel, type Stage } from '@ouradent/shared';
import { useSession } from '../../auth/SessionProvider';
import {
  useAddTask,
  useBoard,
  useCalls,
  useMoveStage,
  useSetNextStep,
  useSetOwner,
  useTasks,
  useTimeline,
  useLeadEvents,
} from '../../lib/data';
import { at, fDT, fromD, fromDT, fShort, toD, toDT } from '../../lib/format';
import type { BoardLead } from '../../lib/types';
import { Icon } from '../../ui/icons';
import {
  CloseX,
  LocalTime,
  OwnerOptions,
  RouteBar,
  SrcBadge,
  useDrawer,
  useToast,
  useWho,
} from '../../ui/kit';
import { askReason } from '../Pipeline';
import { CallRow, TaskRow } from '../rows';
import { LeadAbout } from './LeadAbout';

export function LeadDrawer({ id }: { id: string }) {
  const { can } = useSession();
  const { drawer } = useDrawer();
  const board = useBoard();
  const lead = board.data?.find((l) => l.id === id);

  if (board.isLoading) {
    return (
      <>
        <div className="dhead">
          <h2 id="dtitle">Loading…</h2>
          <CloseX />
        </div>
      </>
    );
  }
  if (!lead) {
    return (
      <>
        <div className="dhead">
          <div>
            <h2 id="dtitle">Lead not found</h2>
            <div className="sm">It may have been merged, or you have no access to it.</div>
          </div>
          <CloseX />
        </div>
      </>
    );
  }

  const initialEdit = drawer?.type === 'lead' ? drawer.edit : undefined;
  return <LeadHub lead={lead} canCalls={can('calls')} initialEdit={initialEdit} />;
}

function LeadHub({
  lead,
  canCalls,
  initialEdit,
}: {
  lead: BoardLead;
  canCalls: boolean;
  initialEdit?: 'complete' | 'change' | undefined;
}) {
  const { me, can } = useSession();
  const toast = useToast();
  const who = useWho();
  const tasks = useTasks();
  const calls = useCalls(canCalls);
  const timeline = useTimeline(lead.id);
  const move = useMoveStage();
  const setOwner = useSetOwner();
  const addTask = useAddTask();
  const [edit, setEdit] = useState<'complete' | 'change' | null>(initialEdit ?? null);

  const leadTasks = (tasks.data ?? [])
    .filter((t) => t.lead_id === lead.id)
    .sort(
      (a, b) =>
        Number(Boolean(a.done_at)) - Number(Boolean(b.done_at)) ||
        +new Date(a.due_at) - +new Date(b.due_at),
    );
  const leadCalls = (calls.data ?? []).filter((c) => c.lead_id === lead.id);
  const reviewCall = leadCalls.find((c) => c.status === 'review');

  const [taskText, setTaskText] = useState('');
  const [taskOwner, setTaskOwner] = useState(me?.id ?? '');
  const [taskDue, setTaskDue] = useState(toD(at(1)));

  function onStage(stage: Stage) {
    const reason = askReason(stage);
    if (reason === null) return;
    move.mutate(
      { lead: lead.id, stage, reason: reason || null },
      { onSuccess: () => toast(`Moved to ${stageLabel(stage)}`), onError: (e) => toast(e.message) },
    );
  }

  function onAddTask() {
    if (!taskText.trim()) return;
    addTask.mutate(
      { lead: lead.id, text: taskText, owner: taskOwner, due: fromD(taskDue) ?? at(1) },
      {
        onSuccess: () => {
          setTaskText('');
          toast('Task added');
        },
        onError: (e) => toast(e.message),
      },
    );
  }

  let box;
  if (lead.step_state === 'review' && reviewCall) {
    box = <ReviewFirst callId={reviewCall.id} />;
  } else if (isClosed(lead.stage)) {
    box = (
      <div className="stepbox">
        <Icon name="cal" />
        <span>
          {lead.stage === 'won'
            ? 'Client. Handed over to onboarding.'
            : `${stageLabel(lead.stage)}${lead.lost_reason ? `: ${lead.lost_reason}` : ''}`}
        </span>
      </div>
    );
  } else if (edit) {
    box = <StepEditor lead={lead} complete={edit === 'complete'} onDone={() => setEdit(null)} />;
  } else {
    const st = lead.step_state;
    box = (
      <>
        <div className={`stepbox ${st !== 'ok' ? 'warn' : ''}`}>
          <Icon name="cal" />
          <span>
            {lead.step_text ? `${lead.step_text}, ${fShort(lead.step_due!)}` : 'No next step yet'}
            <small>
              {lead.step_text
                ? `${who(lead.step_owner).full_name}${st === 'stale' ? ' · date has passed' : ''}`
                : 'Every active lead needs one'}
            </small>
          </span>
        </div>
        <div className="passact">
          {lead.step_text ? (
            <>
              <button className="btn primary sm" onClick={() => setEdit('complete')}>
                Done, set the next one
              </button>
              <button className="btn sm" onClick={() => setEdit('change')}>
                Change
              </button>
            </>
          ) : (
            <button className="btn primary sm" onClick={() => setEdit('change')}>
              Set next step
            </button>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="dhead">
        <div style={{ minWidth: 0 }}>
          <h2 id="dtitle">{lead.clinic}</h2>
          <div className="sm">
            {lead.person} <span className="mk">{lead.market}</span>{' '}
            <SrcBadge source={lead.source} /> <LocalTime city={lead.city} tz={lead.tz} />
          </div>
        </div>
        <CloseX />
      </div>
      <div className="dbody">
        <div className="blk">
          <section className="pass">
            <span className="lbl t">Next step</span>
            <RouteBar stage={lead.stage} />
            <div>{box}</div>
          </section>
        </div>

        <div className="blk" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {lead.phone_e164 && (
            <a className="btn" style={{ textDecoration: 'none' }} href={`tel:${lead.phone_e164}`}>
              <Icon name="phone" /> Call
            </a>
          )}
          {lead.email && (
            <a className="btn" style={{ textDecoration: 'none' }} href={`mailto:${lead.email}`}>
              <Icon name="coldemail" /> Email
            </a>
          )}
        </div>

        <div className="blk grid2">
          <label className="field">
            <span>Stage</span>
            <select
              aria-label="Stage"
              value={lead.stage}
              onChange={(e) => onStage(e.target.value as Stage)}
            >
              {STAGES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Lead owner</span>
            <select
              aria-label="Lead owner"
              value={lead.owner_id ?? ''}
              onChange={(e) =>
                setOwner.mutate(
                  { lead: lead.id, owner: e.target.value },
                  { onSuccess: () => toast('Owner updated'), onError: (er) => toast(er.message) },
                )
              }
            >
              {!lead.owner_id && <option value="">Nobody</option>}
              <OwnerOptions includeExternal={false} />
            </select>
          </label>
        </div>

        <LeadAbout lead={lead} canEdit={canCalls || can('pipeline')} />

        <div className="blk">
          <h3>
            <span className="lbl g">Promises and tasks</span>
          </h3>
          <div className="card list-pad">
            {leadTasks.length ? (
              leadTasks.map((t) => <TaskRow key={t.id} task={t} showLead={false} />)
            ) : (
              <div className="empty">Nothing promised yet.</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Add a promise or task"
              aria-label="New task"
              style={{ flex: '2 1 200px' }}
              value={taskText}
              onChange={(e) => setTaskText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onAddTask()}
            />
            <select
              aria-label="Task owner"
              style={{ flex: '1 1 110px' }}
              value={taskOwner}
              onChange={(e) => setTaskOwner(e.target.value)}
            >
              <OwnerOptions />
            </select>
            <input
              type="date"
              aria-label="Due"
              style={{ flex: '1 1 130px' }}
              value={taskDue}
              onChange={(e) => setTaskDue(e.target.value)}
            />
            <button className="btn" onClick={onAddTask} disabled={addTask.isPending}>
              Add
            </button>
          </div>
        </div>

        <LeadMeetings leadId={lead.id} />

        {leadCalls.length > 0 && (
          <div className="blk">
            <h3>
              <span className="lbl g">Calls</span>
            </h3>
            <div className="card">
              {leadCalls.map((c) => (
                <CallRow key={c.id} call={c} linked />
              ))}
            </div>
          </div>
        )}

        <div className="blk">
          <h3>
            <span className="lbl g">Details</span>
          </h3>
          <div className="panel" style={{ fontSize: 15 }}>
            <p>
              <span style={{ color: 'var(--muted)' }}>Source:</span> {lead.source ?? 'Unknown'}
              {lead.campaign ? ` · ${lead.campaign}` : ''}
            </p>
            <p>
              <span style={{ color: 'var(--muted)' }}>Contact:</span> {lead.email ?? 'no email'}
              {lead.phone_e164 ? ` · ${lead.phone_e164}` : ''}
            </p>
            <p>
              <span style={{ color: 'var(--muted)' }}>Decision-maker on calls:</span>{' '}
              {lead.dm === true
                ? 'yes'
                : lead.dm === false
                  ? 'no, needs a call with them'
                  : 'unknown'}
            </p>
          </div>
        </div>

        <div className="blk">
          <h3>
            <span className="lbl g">History, all channels</span>
          </h3>
          <div className="panel">
            {(timeline.data ?? []).map((e) => (
              <div key={e.id} className="tline">
                <b>{e.kind}</b>
                <div>
                  {e.text}
                  <div style={{ color: 'var(--soft)', fontSize: 13 }}>{fDT(e.at)}</div>
                </div>
              </div>
            ))}
            {!timeline.isLoading && !timeline.data?.length && (
              <div className="empty" style={{ padding: 0 }}>
                No history yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function ReviewFirst({ callId }: { callId: string }) {
  const { open } = useDrawer();
  return (
    <button className="stepbox" onClick={() => open({ type: 'call', id: callId })}>
      <Icon name="phone" />
      <span>
        Review the call first<small>The next step is set when you confirm it</small>
      </span>
      <Icon name="chev" />
    </button>
  );
}

function StepEditor({
  lead,
  complete,
  onDone,
}: {
  lead: BoardLead;
  complete: boolean;
  onDone: () => void;
}) {
  const { me } = useSession();
  const toast = useToast();
  const setStep = useSetNextStep();
  const [text, setText] = useState(complete ? '' : (lead.step_text ?? ''));
  const [due, setDue] = useState(toDT(!complete && lead.step_due ? lead.step_due : at(2, 11)));
  const [owner, setOwner] = useState(lead.step_owner ?? me?.id ?? '');
  const [err, setErr] = useState('');

  function save() {
    const d = fromDT(due);
    if (!text.trim() || !d) {
      setErr('Add what happens next and when.');
      return;
    }
    setStep.mutate(
      { lead: lead.id, text, due: d, owner, complete },
      {
        onSuccess: () => {
          toast('Next step saved');
          onDone();
        },
        onError: (e) => setErr(e.message),
      },
    );
  }

  return (
    <>
      <div className="passedit">
        <label className="field full">
          <span>{complete ? 'What happens next?' : 'Next step'}</span>
          <input
            type="text"
            autoFocus
            value={text}
            placeholder="e.g. Offer call with Andrej"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && save()}
          />
        </label>
        <label className="field">
          <span>When</span>
          <input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} />
        </label>
        <label className="field">
          <span>Who</span>
          <select aria-label="Who" value={owner} onChange={(e) => setOwner(e.target.value)}>
            <OwnerOptions />
          </select>
        </label>
        {err && <p className="err">{err}</p>}
      </div>
      <div className="passact">
        <button className="btn primary sm" onClick={save} disabled={setStep.isPending}>
          Save next step
        </button>
        <button className="btn sm" onClick={onDone}>
          Cancel
        </button>
      </div>
    </>
  );
}

function LeadMeetings({ leadId }: { leadId: string }) {
  const { can } = useSession();
  const { open } = useDrawer();
  const events = useLeadEvents(can('calendar') ? leadId : null);
  if (!can('calendar')) return null;
  const now = Date.now();
  const list = (events.data ?? []).filter((e) => Date.parse(e.end_at) > now - 30 * 86_400_000);
  return (
    <div className="blk">
      <h3>
        <span className="lbl g">Meetings</span>
      </h3>
      <div className="panel">
        {list.map((e) => (
          <button
            key={e.id}
            className="calev lead"
            style={{ marginBottom: 6, padding: '8px 10px' }}
            onClick={() => open({ type: 'event', id: e.id })}
          >
            <b>{e.summary ?? '(no title)'}</b>
            <span>
              {fDT(e.start_at)}
              {Date.parse(e.end_at) < now ? ' · past' : ''}
            </span>
          </button>
        ))}
        {!events.isLoading && !list.length && (
          <div className="empty" style={{ padding: 0 }}>
            No meetings in the calendar.
          </div>
        )}
      </div>
      <button
        className="btn sm"
        style={{ marginTop: 10 }}
        onClick={() => open({ type: 'event', leadId })}
      >
        Schedule a meeting
      </button>
    </div>
  );
}
